const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const STOREFRONT_ACCESS_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
// Curated collection to display (the store has other unrelated products)
const COLLECTION_ID = process.env.REACT_APP_SHOPIFY_COLLECTION_ID;

const STOREFRONT_API_URL = `https://${SHOPIFY_DOMAIN}/api/2024-01/graphql.json`;

async function shopifyFetch(query, variables = {}) {
  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`Shopify API error: ${response.status}`);
  }

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }
  return json.data;
}

const PRODUCT_FIELDS = `
  id
  title
  description
  handle
  productType
  priceRange {
    minVariantPrice { amount, currencyCode }
  }
  images(first: 1) {
    edges { node { url, altText } }
  }
  variants(first: 1) {
    edges { node { id, availableForSale } }
  }
`;

export async function getProducts(first = 250) {
  // Scoped to the curated collection when configured; whole store otherwise
  if (COLLECTION_ID) {
    const query = `
      query GetCollectionProducts($id: ID!, $first: Int!) {
        collection(id: $id) {
          products(first: $first) {
            edges { node { ${PRODUCT_FIELDS} } }
          }
        }
      }
    `;
    const data = await shopifyFetch(query, {
      id: `gid://shopify/Collection/${COLLECTION_ID}`,
      first,
    });
    if (data.collection) {
      return data.collection.products.edges.map(edge => edge.node);
    }
    // Collection missing — fall through to all products
  }
  const query = `
    query GetProducts($first: Int!) {
      products(first: $first) {
        edges { node { ${PRODUCT_FIELDS} } }
      }
    }
  `;
  const data = await shopifyFetch(query, { first });
  return data.products.edges.map(edge => edge.node);
}

export async function createCart(lines = []) {
  const query = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          lines(first: 50) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id, title
                    priceV2 { amount, currencyCode }
                    image { url }
                    product { title }
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount { amount, currencyCode }
            totalAmount { amount, currencyCode }
          }
        }
        userErrors { message, field }
      }
    }
  `;
  const input = lines.length > 0 ? { lines } : {};
  const data = await shopifyFetch(query, { input });
  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }
  return data.cartCreate.cart;
}

// Store collections (Pickleball, Tennis, Hottest Paddles...) with their
// member product ids, for client-side collection filtering on the Shop page.
// Excludes empty collections and the base catalog collection we already show.
export async function getCollections() {
  const query = `
    {
      collections(first: 25) {
        edges {
          node {
            id
            title
            products(first: 250) {
              edges { node { id } }
            }
          }
        }
      }
    }
  `;
  const data = await shopifyFetch(query);
  return data.collections.edges
    .map(e => ({
      id: e.node.id,
      title: e.node.title,
      productIds: e.node.products.edges.map(p => p.node.id),
    }))
    .filter(c =>
      c.productIds.length > 0 &&
      c.id !== `gid://shopify/Collection/${COLLECTION_ID}`
    );
}

// Fetch an existing cart by id — returns null if it expired or belongs
// to a different store (Shopify carts only live ~10 days)
export async function getCart(cartId) {
  const query = `
    query GetCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        lines(first: 50) {
          edges {
            node {
              id, quantity
              merchandise {
                ... on ProductVariant {
                  id, title
                  priceV2 { amount, currencyCode }
                  image { url }
                  product { title }
                }
              }
            }
          }
        }
        cost {
          subtotalAmount { amount, currencyCode }
          totalAmount { amount, currencyCode }
        }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId });
  return data.cart || null;
}

export async function addToCart(cartId, lines) {
  const query = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          lines(first: 50) {
            edges {
              node {
                id, quantity
                merchandise {
                  ... on ProductVariant {
                    id, title
                    priceV2 { amount, currencyCode }
                    image { url }
                    product { title }
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount { amount, currencyCode }
            totalAmount { amount, currencyCode }
          }
        }
        userErrors { message, field }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId, lines });
  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }
  if (!data.cartLinesAdd.cart) {
    throw new Error('Cart no longer exists.');
  }
  return data.cartLinesAdd.cart;
}

export async function updateCartItem(cartId, lines) {
  const query = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id, checkoutUrl
          lines(first: 50) {
            edges {
              node {
                id, quantity
                merchandise {
                  ... on ProductVariant {
                    id, title
                    priceV2 { amount, currencyCode }
                    image { url }
                    product { title }
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount { amount, currencyCode }
            totalAmount { amount, currencyCode }
          }
        }
        userErrors { message, field }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId, lines });
  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }
  return data.cartLinesUpdate.cart;
}

export async function removeFromCart(cartId, lineIds) {
  const query = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id, checkoutUrl
          lines(first: 50) {
            edges {
              node {
                id, quantity
                merchandise {
                  ... on ProductVariant {
                    id, title
                    priceV2 { amount, currencyCode }
                    image { url }
                    product { title }
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount { amount, currencyCode }
            totalAmount { amount, currencyCode }
          }
        }
        userErrors { message, field }
      }
    }
  `;
  const data = await shopifyFetch(query, { cartId, lineIds });
  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }
  return data.cartLinesRemove.cart;
}
